// Import Mixpanel library
import { ENABLE_MIXPANEL_LOGS_PROD, ENABLE_MIXPANEL_LOGS, ENV_PRODUCTION, MIXPANEL_TOKEN } from '@common/config/environments';
import mixpanel from 'mixpanel-browser';

// Logger configuration
const isDevelopment = !ENV_PRODUCTION;
const isProduction = ENV_PRODUCTION;
// Flag para activar logs en desarrollo (puede ser configurado por variable de entorno)
const ENABLE_LOGS_IN_DEV = ENABLE_MIXPANEL_LOGS;
// Flag para activar logs en producción (puede ser configurado por variable de entorno)
const ENABLE_LOGS_IN_PROD = ENABLE_MIXPANEL_LOGS_PROD;

// Logger controlado
const logger = {
    log: (...args: any[]) => {
        if (isDevelopment && ENABLE_LOGS_IN_DEV) {
            console.log(...args);
        } else if (isProduction && ENABLE_LOGS_IN_PROD) {
            console.log(...args);
        }
    },
    warn: (...args: any[]) => {
        if (isDevelopment && ENABLE_LOGS_IN_DEV) {
            console.warn(...args);
        } else if (isProduction && ENABLE_LOGS_IN_PROD) {
            console.warn(...args);
        }
    },
    error: (...args: any[]) => {
        if (isDevelopment && ENABLE_LOGS_IN_DEV) {
            console.error(...args);
        } else if (isProduction && ENABLE_LOGS_IN_PROD) {
            console.error(...args);
        }
    }
};

// Utility functions for Mixpanel integration
const GetEventFactories = () => {
    /* Section Load */
    function SectionLoaded(page: string) {
        return {
            name: 'Section Loaded',
            properties: {
                page
            }
        };
    }

    /* Questions and Answers */
    function QuestionFocused(question: string) {
        return {
            name: 'Question Focused',
            properties: {
                question
            }
        };
    }

    function _QuestionAnswered(question: string, answer: any, from: string) {
        return {
            name: 'Question Answered',
            properties: {
                question,
                answer,
                from
            }
        };
    }

    function QuestionAnsweredFromPage(question: string, answer: any) {
        return _QuestionAnswered(question, answer, 'Page');
    }

    function QuestionsAnsweredFromDropdown(question: string, answer: any) {
        return _QuestionAnswered(question, answer, 'Dropdown');
    }

    function InputUpdated(input: string, value: any) {
        return {
            name: 'Input Updated',
            properties: {
                input,
                value
            }
        };
    }

    function OptionSelected(option: string) {
        return {
            name: 'Option Selected',
            properties: {
                option
            }
        };
    }

    function UserOnboarded() {
        return {
            name: 'User Onboarded'
        };
    }

    return {
        SectionLoaded,
        QuestionFocused,
        QuestionAnsweredFromPage,
        QuestionsAnsweredFromDropdown,
        InputUpdated,
        OptionSelected,
        UserOnboarded
    };
}

const eventFactories = GetEventFactories();


// Initialize Mixpanel with your project token
function initializeMixpanel(): void {
    const token = MIXPANEL_TOKEN;
    
    if (!token) {
        logger.warn('Mixpanel token not found in environment variables');
        return;
    }
    
    try {
        mixpanel.init(token, {
            debug: ENV_PRODUCTION,
            track_pageview: false,
            persistence: 'localStorage',
            ignore_dnt: true // Ignore Do Not Track settings for development
        });
        
        // Verify initialization
        if (typeof mixpanel.track === 'function') {
            logger.log('Mixpanel initialized successfully');
        } else {
            logger.error('Mixpanel initialization failed - track method not available');
        }
    } catch (error) {
        logger.error('Failed to initialize Mixpanel:', error);
    }
}

initializeMixpanel();

// Track an event with optional properties
function trackEvent(event: { name: string; properties?: any }): void {
    const { name, properties } = event;
    
    // Check if mixpanel is properly initialized
    if (!mixpanel) {
        logger.warn('Mixpanel not initialized, event not tracked:', name);
        return;
    }
    
    // Check if mixpanel has the track method and is properly configured
    if (typeof mixpanel.track !== 'function') {
        logger.warn('Mixpanel track method not available, event not tracked:', name);
        return;
    }
    
    // Additional safety check - ensure mixpanel is ready
    if (mixpanel.get_config && typeof mixpanel.get_config === 'function') {
        try {
            const config = mixpanel.get_config();
            if (!config || !config.token) {
                logger.warn('Mixpanel not properly configured, event not tracked:', name);
                return;
            }
        } catch (configError) {
            logger.warn('Could not verify Mixpanel config, proceeding with tracking:', configError);
        }
    }
    
    try {
        mixpanel.track(name, properties);
    } catch (error) {
        logger.error('Failed to track Mixpanel event:', name, error);
    }
}

// Track Section Loaded event with debounce
function debounce<T extends (...args: any[]) => any>(func: T, delay: number): T {
    let timeoutId: ReturnType<typeof setTimeout>;
    return function (this: any, ...args: any[]) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            func.apply(this, args);
        }, delay);
    } as T;
}

const trackSectionLoadedEvent = debounce((page: string) => {
    const event = eventFactories.SectionLoaded(page);
    trackEvent(event);
}, 1000);

// Set or update a user's profile
function setUserProfile(distinctId: string, properties: any): void {
    if (mixpanel && typeof mixpanel.people?.set === 'function') {
        try {
            mixpanel.people.set(distinctId, properties);
        } catch (error) {
            logger.error('Failed to set Mixpanel user profile:', error);
        }
    } else {
        logger.warn('Mixpanel not initialized or tracking disabled, user profile not set');
    }
}

// Create an alias for a user identifier
function aliasUser(distinctId: string, alias: string): void {
    if (mixpanel && typeof mixpanel.alias === 'function') {
        try {
            mixpanel.alias(alias, distinctId);
        } catch (error) {
            logger.error('Failed to set Mixpanel alias:', error);
        }
    } else {
        logger.warn('Mixpanel not initialized or tracking disabled, alias not set');
    }
}

// Increment a numeric property for a user
function incrementProperty(distinctId: string, property: string, incrementBy: number = 1): void {
    if (mixpanel && typeof mixpanel.people?.increment === 'function') {
        try {
            // Mixpanel increment expects a property name and increment value
            // @ts-ignore - Mixpanel types are not correctly defined
            mixpanel.people.increment(distinctId, property, incrementBy);
        } catch (error) {
            logger.error('Failed to increment Mixpanel property:', error);
        }
    } else {
        logger.warn('Mixpanel not initialized or tracking disabled, property not incremented');
    }
}

export {
	initializeMixpanel,
	trackEvent,
	setUserProfile,
	aliasUser,
	incrementProperty,
    trackSectionLoadedEvent
};

export const {
	SectionLoaded,
	QuestionFocused,
	QuestionAnsweredFromPage,
	QuestionsAnsweredFromDropdown,
	InputUpdated,
	OptionSelected,
	UserOnboarded
} = eventFactories;